<template>
  <div v-if="productRecommendations.length">
    <Headline
      v-if="title"
      tag="h2"
      look="h3"
      class="border-b border-gray-400 py-4"
      >{{ title }}</Headline
    >
    <Intersect :threshold="0.5" @enter="trackRecommendationView">
      <component
        :is="wrapper"
        :class="{ 'grid grid-cols-2 gap-4': layout === 'grid' }"
        :has-gradient="layout !== 'twoItemSlider'"
      >
        <div
          v-for="(recommendation, index) in productRecommendations"
          :key="`product-recommendation-${index}`"
          class="group mb-2 flex flex-col justify-between"
          :class="{
            'w-full': layout === 'singleItemSlider',
            'w-1/2': layout === 'twoItemSlider',
            'w-full xs:w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5': layout === 'slider',
          }"
        >
          <ProductCardLegacy
            v-if="recommendation && recommendation.product"
            :id="recommendation.product.id"
            :product="recommendation.product"
            :variant="recommendation.variant"
            :title="
              getFirstAttributeLabel(recommendation.product.attributes, 'brand')
            "
            :name="
              getFirstAttributeLabel(recommendation.product.attributes, 'name')
            "
            class="w-full"
            :image="getImage(recommendation)"
            :price="getPrice(recommendation)"
            placement="PDP-carousel"
            :show-add-to-cart="false"
            :show-available-colors="false"
            :show-description="layout !== 'twoItemSlider'"
            :disable-link="disableLink"
            @click="$emit('click:recommendation', recommendation.product.id)"
            @click:select-item="() => trackRecoClick(recommendation.product)"
          />
          <div v-if="!needsConfiguration(recommendation.product)" class="px-2">
            <FimButton
              type="secondary"
              class="mt-auto w-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              data-testid="virtual-try-on-button"
              @click="addToCart(recommendation.product)"
            >
              {{ $t('pdp.recommendations.button_label') }}
            </FimButton>
          </div>
        </div>
      </component>
    </Intersect>
    <slot />
  </div>
</template>

<script setup lang="ts">
import {
  getAttributeValue,
  type Product,
  getLatestCategory,
  ExistingItemHandling,
} from '@scayle/storefront-nuxt'
import type { PropType } from 'vue'
import HorizontalItemsScroller from '~/components/ui/sliders/HorizontalItemsScroller.vue' // explicit
import type { ProductCategories } from '~/utils/product' // explicit
import { Action } from '~/plugins/toast'
import useUiState from '~/composables/ui/useUiState'

const emit = defineEmits(['click:recommendation'])

const { addItem } = useFimBasket()
const { $i18n, $alert, $currentShop } = useNuxtApp()
const { trackAddToBasket } = await useTrackingEvents()
const { isBasketOverlayOpen, openBasketFlyout } = useUiState()

const props = defineProps({
  recommendationProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
  productRecommendations: {
    type: Array as PropType<ProductRecommendation[]>,
    required: true,
  },
  location: {
    type: String as PropType<'PDP' | 'Pop-Up' | 'Basket'>,
    required: true,
  },
  currentProductCategory: {
    type: String as PropType<ProductCategories | undefined>,
    required: false,
    default: '',
  },
  title: {
    type: String as PropType<string>,
    default: undefined,
  },
  layout: {
    type: String as PropType<
      'slider' | 'grid' | 'singleItemSlider' | 'twoItemSlider'
    >,
    default: 'slider',
    validate: (value: string) =>
      ['slider', 'grid', 'singleItemSlider', 'twoItemSlider'].includes(value),
  },
  disableLink: {
    type: Boolean,
    default: false,
  },
})

const { getCombinedProductPrice } = await useProductPrice()
const { trackSelectItem, trackViewItemList } = await useTrackingEvents()
const isListViewTrackedAlready = ref(false)
const productRecommendations = toRef(props, 'productRecommendations')

const needsConfiguration = (product: Product) => {
  if (isContactLens(product)) {
    return true
  }

  if (
    isCorrectionalGlasses(product) &&
    !isHomeTryOnEligible(product, $currentShop)
  ) {
    return true
  }

  return (product?.variants?.length ?? 1) > 1
}

const ids = computed(() => {
  if (!Array.isArray(productRecommendations.value)) {
    return []
  }

  return (
    productRecommendations.value
      ?.filter((p) => !p.variant && !needsConfiguration(p.product))
      ?.map((p) => p.product.id) ?? []
  )
})

const { data: products } = await useProductsByIds({
  params: {
    ids: ids.value,
  },
  options: { lazy: true, watch: [ids] },
})

/**
 * Wrapper to get price for recommendation
 * show lowest price if we have no specific variant to show
 * otherwise show variant price
 */
const getPrice = (data?: ProductRecommendation) => {
  if (data?.variant?.id) {
    return getCombinedProductPrice(data.product, data.variant)
  }

  if (data?.product) {
    return getCombinedProductPrice(data.product)
  }

  return data?.product.priceRange?.min
}

/**
 * Wrapper to get image for recommendation
 */
const getImage = (data?: ProductRecommendation) => {
  const images = data?.product?.images ?? []

  if (data?.variant?.referenceKey) {
    // try to find image for variant
    const image = images.find(
      (i) =>
        getAttributeValue(i.attributes, 'imageVariantReferenceKey') ===
        data?.variant?.referenceKey,
    )

    if (image) {
      return image
    }
  }

  return getImageFromList(images, 'model', 'front', true)
}

const wrapper = ['singleItemSlider', 'slider', 'twoItemSlider'].includes(
  props.layout,
)
  ? HorizontalItemsScroller
  : 'div'

const addToCart = async (product: Product) => {
  try {
    const variant =
      product.variants?.[0] ||
      products.value?.find((p) => p.id === product.id)?.variants?.[0]
    const variantId = variant?.id

    if (!variantId) {
      throw new Error($i18n.t('rx.home_try_on.error.no_variant_selected'))
    }

    const addItemPayload = {
      variantId: variantId,
      quantity: 1,
      customData: {
        productType: getProductType(product),
      },
    }

    await addItem({
      ...addItemPayload,
      existingItemHandling: ExistingItemHandling.AddQuantityToExisting,
    })

    trackAddToBasket(product, 1, variant)

    if (isBasketOverlayOpen.value) {
      $alert.show($i18n.t('pdp.recommendations.toast_label'), Action.confirm)
    } else {
      openBasketFlyout()
    }
  } catch (error) {
    console.error('[RXCONFIGURATOR ADD]: ', error)
  }
}

const getTrackingLocation = (loc: typeof props.location) => {
  switch (loc) {
    case 'Basket':
      return 'Basket Page'
    case 'PDP':
      return 'Product Page'
    case 'Pop-Up':
      return 'Basket Overlay'
  }
}

const getTrackingListingMetaData = () => ({
  name: [RecommendationListingMetadataType, getTrackingLocation(props.location)]
    .filter(Boolean)
    .join(' | '),
  id: [props.title, props.recommendationProduct.referenceKey]
    .filter(Boolean)
    .join(' | '),
})

const trackRecommendationView = () => {
  if (
    isListViewTrackedAlready.value ||
    !Array.isArray(productRecommendations.value)
  ) {
    return
  }

  const products =
    productRecommendations.value?.map(
      (recommendation) => recommendation.product,
    ) ?? []
  trackViewItemList(products, getTrackingListingMetaData())
  isListViewTrackedAlready.value = true
}

const trackRecoClick = (product: Product) => {
  trackSelectItem(
    product,
    getLatestCategory(product.categories),
    getTrackingListingMetaData(),
  )
}
</script>

<style scoped>
.slide {
  flex: 0 0 auto;
}
</style>
